import type {
  DreGerencialApiResponse,
  DREGerencialView,
  QualidadeDadosDreView,
  ReceitaDetalhadaView,
} from "../types/dre-gerencial.types";

export function toNumber(value: unknown): number {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  let s = String(value).trim();
  if (s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function mapDespesasCategorias(porCategoria: Record<string, number> | undefined): {
  despesaMarketing: number;
  despesaInfraestrutura: number;
  despesaOperacional: number;
  despesaServicos: number;
  despesaAdministrativo: number;
  despesaOutros: number;
  despesasPorCategoria: Record<string, number>;
} {
  const despesasPorCategoria: Record<string, number> = { ...(porCategoria ?? {}) };

  let despesaMarketing = 0;
  let despesaInfraestrutura = 0;
  let despesaOperacional = 0;
  let despesaServicos = 0;
  let despesaAdministrativo = 0;
  let despesaOutros = 0;

  for (const [k, raw] of Object.entries(despesasPorCategoria)) {
    const v = toNumber(raw);
    despesasPorCategoria[k] = v;
    switch (k) {
      case "MARKETING":
        despesaMarketing += v;
        break;
      case "INFRAESTRUTURA":
        despesaInfraestrutura += v;
        break;
      case "OPERACIONAL":
        despesaOperacional += v;
        break;
      case "SERVICOS":
        despesaServicos += v;
        break;
      case "ADMINISTRATIVO":
        despesaAdministrativo += v;
        break;
      case "FRETE":
      case "EVENTOS":
      case "FORNECEDORES":
      case "IMPOSTOS":
      case "OUTROS":
      case "AJUSTE_LANCAMENTO":
        despesaOutros += v;
        break;
      default:
        despesaOutros += v;
    }
  }

  return {
    despesaMarketing,
    despesaInfraestrutura,
    despesaOperacional,
    despesaServicos,
    despesaAdministrativo,
    despesaOutros,
    despesasPorCategoria,
  };
}

function mapReceitaDetalhada(
  api: DreGerencialApiResponse["receitaDetalhada"] | undefined,
): ReceitaDetalhadaView {
  const loja = api?.loja;
  const insc = api?.inscricoes;
  return {
    loja: {
      receitaCobrada: toNumber(loja?.receitaCobrada),
      receitaComercial: toNumber(loja?.receitaComercial),
      jurosParcelamento: toNumber(loja?.jurosParcelamento),
    },
    inscricoes: {
      receitaCobrada: toNumber(insc?.receitaCobrada),
      receitaComercial: toNumber(insc?.receitaComercial),
      jurosParcelamento: toNumber(insc?.jurosParcelamento),
    },
  };
}

function mapQualidadeDados(
  api: DreGerencialApiResponse["qualidadeDados"] | undefined,
): QualidadeDadosDreView {
  return {
    pedidosComReceitaNoPeriodo: toNumber(api?.pedidosComReceitaNoPeriodo),
    pedidosComCmvSnapshot: toNumber(api?.pedidosComCmvSnapshot),
    pedidosReceitaSemCmvSnapshot: toNumber(api?.pedidosReceitaSemCmvSnapshot),
  };
}

/** Rótulo para chaves de `porCategoria` (backend / Prisma). */
export function rotuloCategoriaDespesa(key: string): string {
  const labels: Record<string, string> = {
    MARKETING: "Marketing",
    FRETE: "Frete",
    INFRAESTRUTURA: "Infraestrutura",
    SERVICOS: "Serviços",
    EVENTOS: "Eventos",
    FORNECEDORES: "Fornecedores",
    IMPOSTOS: "Impostos",
    OPERACIONAL: "Operacional",
    OUTROS: "Outros",
    AJUSTE_LANCAMENTO: "Ajustes de lançamento",
    ADMINISTRATIVO: "Administrativo",
  };
  return labels[key] ?? key.replace(/_/g, " ");
}

export function mapDreApiToView(api: DreGerencialApiResponse): DREGerencialView {
  const rb = api.receitaBruta;
  const ded = api.deducoes;
  const despesasMap = mapDespesasCategorias(api.despesasOperacionais?.porCategoria);

  return {
    receitaBrutaTotal: toNumber(rb?.total),
    receitaProdutos: toNumber(rb?.vendasProdutosServicos),
    receitaInscricoes: toNumber(rb?.inscricoesEvento),
    receitaFretes: toNumber(rb?.fretes),
    receitaPatrocinios: toNumber(rb?.patrocinios),
    receitaAjustes: toNumber(rb?.receitaAjustesManual),

    deducoesTotal: toNumber(ded?.total),
    taxasGateway: toNumber(ded?.taxasGateway),
    estornos: toNumber(ded?.estornosEcancelamentosFinanceiros),

    receitaLiquida: toNumber(api.receitaLiquida),

    cmvTotal: toNumber(api.cmv),

    lucroBruto: toNumber(api.lucroBruto),
    margemBruta: toNumber(api.indicadores?.margemBrutaRatio),

    despesasTotal: toNumber(api.despesasOperacionais?.total),
    despesaMarketing: despesasMap.despesaMarketing,
    despesaInfraestrutura: despesasMap.despesaInfraestrutura,
    despesaOperacional: despesasMap.despesaOperacional,
    despesaServicos: despesasMap.despesaServicos,
    despesaAdministrativo: despesasMap.despesaAdministrativo,
    despesaOutros: despesasMap.despesaOutros,
    despesasPorCategoria: despesasMap.despesasPorCategoria,

    lucroOperacional: toNumber(api.lucroOperacional),
    margemOperacional: toNumber(api.margemOperacional),
    participacaoCmv: toNumber(api.indicadores?.participacaoCmvNaReceitaLiquida),

    receitaDetalhada: mapReceitaDetalhada(api.receitaDetalhada),
    qualidadeDados: mapQualidadeDados(api.qualidadeDados),
  };
}
